// Currency utility functions for formatting values based on organization currency

export interface CurrencyConfig {
  code: string;
  symbol: string;
  name: string;
}

export const CURRENCY_CONFIGS: Record<string, CurrencyConfig> = {
  USD: { code: "USD", symbol: "$", name: "US Dollar" },
  GBP: { code: "GBP", symbol: "£", name: "British Pound" },
  EUR: { code: "EUR", symbol: "€", name: "Euro" },
};

/**
 * Format a currency value with the appropriate symbol and formatting
 *
 * Features:
 * - Automatically adds thousand separators (e.g., 53,100)
 * - Drops .00 for whole numbers to reduce visual noise
 * - Supports compact notation (K/M/B/T) for large values
 * - Preserves decimal places only when needed
 */
export const formatCurrency = (
  value: number | undefined | null,
  currencyCode = "USD",
  options: {
    showSymbol?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    compact?: boolean;
  } = {},
): string => {
  if (value === undefined || value === null || isNaN(value)) {
    return "-";
  }

  // Convert string numbers to actual numbers and handle .00 decimals
  const numericValue = typeof value === "string" ? parseFloat(value) : value;

  const {
    showSymbol = true,
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    compact = false,
  } = options;

  const config = CURRENCY_CONFIGS[currencyCode] || CURRENCY_CONFIGS.USD;

  if (compact) {
    // Trillions
    if (Math.abs(numericValue) >= 1000000000000) {
      const trillions = numericValue / 1000000000000;
      const formattedValue = trillions.toLocaleString("en-US", {
        minimumFractionDigits: Math.abs(trillions) >= 10 ? 0 : 1,
        maximumFractionDigits: Math.abs(trillions) >= 10 ? 0 : 1,
      });
      return showSymbol
        ? `${config.symbol}${formattedValue}T`
        : `${formattedValue}T`;
    }

    // Billions
    if (Math.abs(numericValue) >= 1000000000) {
      const billions = numericValue / 1000000000;
      const formattedValue = billions.toLocaleString("en-US", {
        minimumFractionDigits: Math.abs(billions) >= 10 ? 0 : 1,
        maximumFractionDigits: Math.abs(billions) >= 10 ? 0 : 1,
      });
      return showSymbol
        ? `${config.symbol}${formattedValue}B`
        : `${formattedValue}B`;
    }

    // Millions
    if (Math.abs(numericValue) >= 1000000) {
      const millions = numericValue / 1000000;
      const formattedValue = millions.toLocaleString("en-US", {
        minimumFractionDigits: Math.abs(millions) >= 10 ? 0 : 1,
        maximumFractionDigits: Math.abs(millions) >= 10 ? 0 : 1,
      });
      return showSymbol
        ? `${config.symbol}${formattedValue}M`
        : `${formattedValue}M`;
    }

    // Thousands (only show K for values >= 100,000 as per requirement)
    if (Math.abs(numericValue) >= 100000) {
      const thousands = numericValue / 1000;
      const formattedValue = thousands.toLocaleString("en-US", {
        minimumFractionDigits: Math.abs(thousands) >= 10 ? 0 : 1,
        maximumFractionDigits: Math.abs(thousands) >= 10 ? 0 : 1,
      });
      return showSymbol
        ? `${config.symbol}${formattedValue}K`
        : `${formattedValue}K`;
    }
  }

  // For whole numbers, ensure we don't show unnecessary decimals
  // Round to 2 decimal places first to handle database numeric values like 35400.00
  const roundedValue = Math.round(numericValue * 100) / 100;
  const isWholeNumber = roundedValue % 1 === 0;

  // When compact mode is enabled OR it's a whole number, always drop decimals for cleaner display
  const actualMinFractionDigits =
    compact || isWholeNumber ? 0 : minimumFractionDigits;
  const actualMaxFractionDigits =
    compact || isWholeNumber ? 0 : maximumFractionDigits;

  // Force remove trailing .00 for cleaner display
  let formattedValue = numericValue.toLocaleString("en-US", {
    minimumFractionDigits: actualMinFractionDigits,
    maximumFractionDigits: actualMaxFractionDigits,
  });

  // Additional check to remove .00 if it exists
  if (formattedValue.endsWith(".00")) {
    formattedValue = formattedValue.slice(0, -3);
  }

  return showSymbol ? `${config.symbol}${formattedValue}` : formattedValue;
};

/**
 * Get currency symbol for a given currency code
 */
export const getCurrencySymbol = (currencyCode = "USD"): string => {
  const config = CURRENCY_CONFIGS[currencyCode] || CURRENCY_CONFIGS.USD;
  return config.symbol;
};

/**
 * Get currency name for a given currency code
 */
export const getCurrencyName = (currencyCode = "USD"): string => {
  const config = CURRENCY_CONFIGS[currencyCode] || CURRENCY_CONFIGS.USD;
  return config.name;
};
